import React from "react";
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Space,
  Typography,
} from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  BankOutlined,
  SettingOutlined,
  LogoutOutlined,
  CrownOutlined,
  GlobalOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./SuperAdminAppShell.css";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const SuperAdminAppShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // SuperAdmin menu items
  const menuItems = [
    {
      key: "/dashboard/superadmin",
      icon: <DashboardOutlined />,
      label: "SuperAdmin Dashboard",
      onClick: () => navigate("/dashboard/superadmin"),
    },
    {
      key: "/superadmin/companies",
      icon: <BankOutlined />,
      label: "Companies Management",
      onClick: () => navigate("/dashboard/superadmin/companies"),
    },
    {
      key: "users",
      icon: <TeamOutlined />,
      label: "User Management",
      children: [
        {
          key: "/superadmin/users",
          label: "All Users",
          onClick: () => navigate("/dashboard/superadmin/users"),
        },
        {
          key: "/superadmin/admins",
          label: "Company Admins",
          onClick: () => navigate("/dashboard/superadmin/admins"),
        },
      ],
    },
    {
      key: "system",
      icon: <GlobalOutlined />,
      label: "System Management",
      children: [
        {
          key: "/superadmin/analytics",
          label: "System Analytics",
          onClick: () => navigate("/dashboard/superadmin/analytics"),
        },
        {
          key: "/superadmin/settings",
          label: "System Settings",
          onClick: () => navigate("/dashboard/superadmin/settings"),
        },
      ],
    },
  ];

  // Profile dropdown menu
  const profileMenu = {
    items: [
      {
        key: "profile",
        icon: <UserOutlined />,
        label: "Profile Settings",
        onClick: () => navigate("/dashboard/superadmin/profile"),
      },
      {
        key: "settings",
        icon: <SettingOutlined />,
        label: "Account Settings",
        onClick: () => navigate("/dashboard/superadmin/account-settings"),
      },
      {
        type: "divider",
      },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Logout",
        onClick: () => {
          logout();
          navigate("/login");
        },
      },
    ],
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Layout className="superadmin-app-shell">
      {/* SuperAdmin Header */}
      <Header className="superadmin-header">
        <div className="superadmin-header-left">
          <CrownOutlined className="superadmin-logo-icon" />
          <Title level={3} className="superadmin-title">
            Travel CRM SuperAdmin
          </Title>
        </div>

        <div className="superadmin-header-right">
          <Space size="large">
            <div className="superadmin-user-info">
              <Text className="superadmin-welcome">Welcome back,</Text>
              <Text strong className="superadmin-username">
                {user?.name || "SuperAdmin"}
              </Text>
            </div>

            <Dropdown menu={profileMenu} placement="bottomRight" arrow>
              <Button type="text" className="superadmin-profile-btn">
                <Avatar icon={<UserOutlined />} className="superadmin-avatar" />
              </Button>
            </Dropdown>

            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="superadmin-logout-btn"
            >
              Logout
            </Button>
          </Space>
        </div>
      </Header>

      <Layout className="superadmin-main-layout">
        {/* SuperAdmin Sidebar */}
        <Sider width={280} className="superadmin-sidebar" theme="light">
          <div className="superadmin-sidebar-header">
            <GlobalOutlined className="superadmin-sidebar-icon" />
            <div className="superadmin-sidebar-text">
              <Text strong>Multi-Tenant Control</Text>
              <Text type="secondary" className="superadmin-sidebar-subtitle">
                Manage all companies
              </Text>
            </div>
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={["companies", "users", "system"]}
            items={menuItems}
            className="superadmin-menu"
          />
        </Sider>

        {/* SuperAdmin Content */}
        <Content className="superadmin-content">
          <div className="superadmin-content-wrapper">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SuperAdminAppShell;
